import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';

interface User {
  _id: string;
  /** Some auth/JWT payloads return `id` instead of `_id`; keep both accessible. */
  id?: string;
  firstName: string;
  lastName: string;
  name: string; // Full name for backward compatibility
  email: string;
  role: 'admin' | 'coach' | 'player' | 'parent';
  avatar?: string;
  sports: string[];
  verified: boolean;
  status: string;
}

interface Impersonation {
  id: string;
  name: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  setUserData: (userData: User, token?: string) => void;
  loading: boolean;
  /** When set, the current session is a parent acting as one of their children. */
  impersonatedBy: Impersonation | null;
  /** Parent switches into a child. Returns the child user object. */
  actAsChild: (childId: string) => Promise<User>;
  /** Restore the parent's token/user after acting-as. */
  stopImpersonating: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const PARENT_TOKEN_KEY = 'parentToken';
const PARENT_USER_KEY = 'parentUser';
const IMPERSONATION_KEY = 'impersonation';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [impersonatedBy, setImpersonatedBy] = useState<Impersonation | null>(() => {
    try {
      const raw = localStorage.getItem(IMPERSONATION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  useEffect(() => {
    // Check for existing token on app load
    const token = localStorage.getItem('token');
    if (token) {
      validateToken();
    } else {
      setLoading(false);
    }
  }, []);

  const validateToken = async () => {
    try {
      const response = await api.get('/auth/me');
      if (response.data.success) {
        setUser(response.data.data.user);
      } else {
        localStorage.removeItem('token');
      }
    } catch (error) {
      // Token is invalid or backend unreachable, remove it
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    
    if (response.data.success) {
      const { token, user } = response.data.data;
      localStorage.setItem('token', token);
      setUser(user);
      return user; // Return user data for redirect logic
    } else {
      throw new Error(response.data.error?.message || 'Login failed');
    }
  };

  const logout = async () => {
    try {
      // Call backend logout endpoint
      await api.post('/auth/logout');
    } catch (error) {
      // Even if backend call fails, still logout locally
      console.error('Logout error:', error);
    } finally {
      // Always clear local storage and user state, including any parent-impersonation trail.
      localStorage.removeItem('token');
      localStorage.removeItem(PARENT_TOKEN_KEY);
      localStorage.removeItem(PARENT_USER_KEY);
      localStorage.removeItem(IMPERSONATION_KEY);
      setImpersonatedBy(null);
      setUser(null);
    }
  };

  const actAsChild = async (childId: string): Promise<User> => {
    // Backup parent token + user before we swap them out.
    const parentToken = localStorage.getItem('token');
    if (!parentToken) throw new Error('Not authenticated');
    if (user) localStorage.setItem(PARENT_USER_KEY, JSON.stringify(user));
    localStorage.setItem(PARENT_TOKEN_KEY, parentToken);

    const resp = await api.post(`/users/children/${childId}/switch`);
    if (!resp.data?.success) throw new Error(resp.data?.error?.message || 'Switch failed');

    const { token, user: childUser, impersonatedBy: imp } = resp.data.data;
    localStorage.setItem('token', token);
    localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(imp));
    setImpersonatedBy(imp);
    setUser(childUser);
    return childUser;
  };

  const stopImpersonating = () => {
    const parentToken = localStorage.getItem(PARENT_TOKEN_KEY);
    const parentUserRaw = localStorage.getItem(PARENT_USER_KEY);
    if (!parentToken) return;

    localStorage.setItem('token', parentToken);
    localStorage.removeItem(PARENT_TOKEN_KEY);
    localStorage.removeItem(PARENT_USER_KEY);
    localStorage.removeItem(IMPERSONATION_KEY);
    setImpersonatedBy(null);

    if (parentUserRaw) {
      try { setUser(JSON.parse(parentUserRaw)); }
      catch { /* fall through to re-validate */ }
    }
    // Re-validate the restored parent token in the background to refresh user data.
    validateToken();
  };

  const setUserData = (userData: User, token?: string) => {
    if (token) {
      localStorage.setItem('token', token);
    }
    setUser(userData);
  };

  const value = {
    user,
    login,
    logout,
    setUserData,
    loading,
    impersonatedBy,
    actAsChild,
    stopImpersonating,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};